import React from "react";
import styles from "./Chart.module.css";
import { seriesColor } from "./chartTheme";

/**
 * Horizontal 100%-stacked bar (no canvas) with an optional legend — used for
 * distribution readouts (users by role, active vs inactive) and, with
 * `variant="meter"`, for a single-value utilisation meter.
 *
 *   segments = [{ label: "Students", value: 12400, caption: "12,400 users",
 *                 color: "#3b82f6" }]           // share of `total`
 *   segments = [{ label: "Used", pct: 42, color: "var(--gradient-orange)" }]
 *                                               // explicit percentage
 *
 * `color` accepts any CSS background, so gradients work as well as solids.
 */
export default function SegmentedBar({
  segments = [],
  total,
  variant = "bar",
  showLegend = true,
  showPercent = false,
  legendLayout = "grid",
  minVisiblePct = 0,
  loading = false,
  error = null,
  emptyText = "No data available.",
}) {
  const sum =
    total ?? segments.reduce((acc, segment) => acc + (segment.value || 0), 0);

  const parts = segments.map((segment, index) => {
    const pct =
      segment.pct != null ? segment.pct : sum > 0 ? ((segment.value || 0) / sum) * 100 : 0;
    return {
      ...segment,
      pct,
      width: pct > 0 ? Math.max(pct, minVisiblePct) : 0,
      color: segment.color || seriesColor(index),
    };
  });

  if (loading) return <div className={styles.frameMuted}>Loading…</div>;
  if (error) return <div className={styles.frameMuted}>{error}</div>;
  if (!parts.length) return <div className={styles.frameMuted}>{emptyText}</div>;

  return (
    <>
      <div className={variant === "meter" ? styles.meterTrack : styles.barTrack}>
        {parts.map((part) =>
          part.width > 0 ? (
            <div
              key={part.label}
              className={variant === "meter" ? styles.meterFill : undefined}
              style={{ width: `${part.width}%`, background: part.color }}
              title={`${part.label} ${Math.round(part.pct)}%`}
            />
          ) : null,
        )}
      </div>

      {showLegend && (
        <div
          className={legendLayout === "row" ? styles.legendRow : styles.legendGrid}
        >
          {parts.map((part) => (
            <div key={part.label}>
              <div className={styles.legendTop}>
                <span className={styles.legendDot} style={{ background: part.color }} />
                {part.label}
                {showPercent && ` (${part.pct}%)`}
              </div>
              {part.caption && <div className={styles.legendCaption}>{part.caption}</div>}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
